import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminProductService } from '../../../services/adminProductService';
import { ArrowLeft, Save, Upload, Loader2, Trash2 } from 'lucide-react';

const EditProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [previewImage, setPreviewImage] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        is_active: true,
        image: null,
        // New Fields
        flavor_notes: [''],
        body: '',
        acidity: '',
        aftertaste: '',
        recommended_brew: ['']
    });

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await adminProductService.getById(id);
                const product = response.data.product;

                setFormData({
                    name: product.name,
                    description: product.description,
                    is_active: !!product.is_active,
                    image: null,

                    // Parse arrays or use defaults
                    flavor_notes: Array.isArray(product.flavor_notes) && product.flavor_notes.length > 0
                        ? product.flavor_notes
                        : [''],
                    body: product.body || '',
                    acidity: product.acidity || '',
                    aftertaste: product.aftertaste || '',
                    recommended_brew: Array.isArray(product.recommended_brew) && product.recommended_brew.length > 0
                        ? product.recommended_brew
                        : ['']
                });

                if (product.image) {
                    setPreviewImage(product.image);
                }
            } catch (error) {
                console.error('Failed to fetch product:', error);
                toast.error('Failed to load product.');
                navigate('/admin/products');
            } finally {
                setIsFetching(false);
            }
        };

        fetchProduct();
    }, [id, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Dynamic List Handlers
    const handleDynamicListChange = (field, index, value) => {
        const newList = [...formData[field]];
        newList[index] = value;
        setFormData(prev => ({ ...prev, [field]: newList }));
    };

    const addDynamicItem = (field) => {
        setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
    };

    const removeDynamicItem = (field, index) => {
        const newList = [...formData[field]];
        if (newList.length > 1) {
            newList.splice(index, 1);
            setFormData(prev => ({ ...prev, [field]: newList }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, image: file }));
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('description', formData.description);
            data.append('is_active', formData.is_active ? 1 : 0);

            data.append('body', formData.body);
            data.append('acidity', formData.acidity);
            data.append('aftertaste', formData.aftertaste);

            const cleanFlavorNotes = formData.flavor_notes.filter(item => item.trim() !== '');
            const cleanBrew = formData.recommended_brew.filter(item => item.trim() !== '');

            data.append('flavor_notes', JSON.stringify(cleanFlavorNotes));
            data.append('recommended_brew', JSON.stringify(cleanBrew));

            if (formData.image) {
                data.append('image', formData.image);
            }

            await adminProductService.update(id, data);
            toast.success('Product updated successfully!');
            // navigate('/admin/products');
        } catch (error) {
            console.error('Failed to update product:', error);
            toast.error('Failed to update product.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;

        try {
            await adminProductService.delete(id);
            toast.success('Product deleted successfully!');
            navigate('/admin/products');
        } catch (error) {
            console.error('Failed to delete:', error);
            toast.error('Failed to delete product.');
        }
    };

    if (isFetching) {
        return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-8 font-sans text-[#31415A] min-w-0">
            <div className="max-w-5xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
                    <div>
                        <Link to="/admin/products" className="inline-flex items-center text-sm text-gray-500 hover:text-[#31415A] mb-2 transition-colors">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Products
                        </Link>
                        <h1 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#31415A] to-[#4B6A9C]">
                            Edit Product
                        </h1>
                    </div>
                    <div className="flex gap-3 w-full md:w-auto">
                        <button
                            onClick={handleDelete}
                            className="flex-1 md:flex-none inline-flex justify-center items-center px-4 py-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all font-medium"
                        >
                            <Trash2 className="w-5 h-5 mr-2" />
                            Delete
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isLoading}
                            className="flex-1 md:flex-none inline-flex justify-center items-center px-6 py-3 bg-[#31415A] text-white rounded-xl hover:bg-[#2C3A4F] transition-all shadow-lg hover:shadow-xl disabled:opacity-70"
                        >
                            {isLoading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
                            Update
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column: Form Info */}
                    <div className="lg:col-span-2 space-y-6">

                        {/* Basic Info Card */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <h2 className="text-lg font-bold mb-4 pb-2 border-b border-gray-100">Basic Information</h2>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Product Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#31415A] focus:border-transparent outline-none transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                                    <textarea
                                        name="description"
                                        rows="5"
                                        value={formData.description}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#31415A] focus:border-transparent outline-none transition-all resize-y font-sans text-sm leading-relaxed"
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        {/* Specific Attributes Card */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <h2 className="text-lg font-bold mb-4 pb-2 border-b border-gray-100">Product Attributes</h2>
                            <div className="space-y-6">

                                {/* Flavor Notes (Dynamic List) */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Flavor Notes</label>
                                        <button
                                            type="button"
                                            onClick={() => addDynamicItem('flavor_notes')}
                                            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1 rounded transition-colors"
                                        >
                                            + Add Note
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        {formData.flavor_notes.map((note, index) => (
                                            <div key={index} className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={note}
                                                    onChange={(e) => handleDynamicListChange('flavor_notes', index, e.target.value)}
                                                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-[#31415A] focus:border-[#31415A] text-sm"
                                                    placeholder="e.g. Dark Chocolate"
                                                />
                                                {formData.flavor_notes.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeDynamicItem('flavor_notes', index)}
                                                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-5">
                                    {/* Body */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Body</label>
                                        <input
                                            type="text"
                                            name="body"
                                            value={formData.body}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-[#31415A] focus:border-[#31415A] text-sm"
                                            placeholder="e.g. Medium, Syrupy"
                                        />
                                    </div>

                                    {/* Acidity */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Acidity</label>
                                        <input
                                            type="text"
                                            name="acidity"
                                            value={formData.acidity}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-[#31415A] focus:border-[#31415A] text-sm"
                                            placeholder="e.g. Low, Bright"
                                        />
                                    </div>
                                </div>

                                {/* Recommended Brew (Dynamic List) */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Recommended Brew</label>
                                        <button
                                            type="button"
                                            onClick={() => addDynamicItem('recommended_brew')}
                                            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1 rounded transition-colors"
                                        >
                                            + Add Brew
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        {formData.recommended_brew.map((brew, index) => (
                                            <div key={index} className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={brew}
                                                    onChange={(e) => handleDynamicListChange('recommended_brew', index, e.target.value)}
                                                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-[#31415A] focus:border-[#31415A] text-sm"
                                                    placeholder="e.g. V60"
                                                />
                                                {formData.recommended_brew.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeDynamicItem('recommended_brew', index)}
                                                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Aftertaste */}
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Aftertaste</label>
                                    <input
                                        type="text"
                                        name="aftertaste"
                                        value={formData.aftertaste}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-[#31415A] focus:border-[#31415A] text-sm"
                                        placeholder="e.g. Sweet, Clean"
                                    />
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Right Column: Image & Status */}
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Product Image</h2>
                            <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 hover:border-[#31415A] transition-colors group">
                                {previewImage ? (
                                    <img src={previewImage} alt="Preview" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                                        <Upload className="w-8 h-8 mb-2" />
                                        <span className="text-xs font-medium">Click to upload</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    onChange={handleImageChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    accept="image/*"
                                />
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Status</h2>
                            <div className="flex items-center justify-between">
                                <span className={`text-sm font-bold ${formData.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                                    {formData.is_active ? 'Active' : 'Draft'}
                                </span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={formData.is_active}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#31415A]"></div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditProduct;
